import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';

import { handleFirestoreError } from './handleFirestoreError';

type Status = 'idle' | 'loading' | 'fetching' | 'error' | 'critical-error' | 'unknown';

type QueryHelper<TData = unknown> = {
  queryStatus: Status;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isCriticalError: boolean;
  errorMessage: string | null;
  previousData: TData | null; // Added previousData
};

type Query<TData = unknown, TError = AxiosError> = UseQueryResult<TData, TError>;

const isEnabled = (enabledStatus: boolean[] | undefined, idx: number): boolean => {
  if (!enabledStatus || enabledStatus.length <= idx) return true;
  return enabledStatus[idx];
};

// eslint-disable-next-line max-len
const isLoading = <TData, TError>({ queries, enabledStatus }: QueryHelperParams<TData, TError>): boolean => queries.some((q, idx) => q.isLoading && isEnabled(enabledStatus, idx));

const isError = <TData, TError>(...queries: Query<TData, TError>[]): boolean => queries.some((q) => q.isError);

// eslint-disable-next-line max-len
const isCriticalError = <TData, TError>(...queries: Query<TData, TError>[]): boolean => queries.some((q) => q.error && (q.error as unknown as AxiosError).response?.status !== 404);

type QueryHelperParams<TData = unknown, TError = AxiosError> = {
  queries: Query<TData, TError>[];
  enabledStatus?: boolean[];
  initialState?: Status;
};

export const useQueryHelper = <TData, TError>({
  queries,
  enabledStatus,
  initialState = 'idle',
}: QueryHelperParams<TData, TError>): QueryHelper<TData> => {
  const [queryStatus, setQueryStatus] = useState<Status>(initialState);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previousData, setPreviousData] = useState<TData | null>(null); // Track previous data

  useEffect(() => {
    if (isLoading({ queries, enabledStatus })) {
      setQueryStatus('loading');
      return;
    }

    if (isError(...queries)) {
      setQueryStatus('error');

      if (isCriticalError(...queries)) {
        setQueryStatus('critical-error');
        setErrorMessage(handleFirestoreError('critical-error'));
      }
      return;
    }

    // Update previousData if a querys has new data
    const latestQueryWithData = queries.find((q) => q.data);
    if (latestQueryWithData?.data) {
      setPreviousData(latestQueryWithData.data as TData);
    }

    setQueryStatus('idle'); // Default to idle if no issues
  }, [queries, enabledStatus]);

  return {
    queryStatus,
    errorMessage,
    isLoading: queryStatus === 'loading',
    isFetching: queryStatus === 'loading' || queryStatus === 'fetching',
    isError: queryStatus === 'error' || queryStatus === 'critical-error',
    isCriticalError: queryStatus === 'critical-error',
    previousData, // Include previous data in the return value
  };
};
